import React from 'react';
import { ArrowRight, ChevronRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { heroData } from '../data/mock';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  })
};

const Hero = () => {
  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[74vh] lg:min-h-[72vh] pt-24 lg:pt-24 pb-10 lg:pb-12 overflow-hidden"
      style={{ backgroundColor: '#0b1120' }}
    >
      {/* Background grid */}
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-50" />

      {/* Hero visual — MOBILE: full-width, toned-down, atmospheric */}
      <div
        className="absolute inset-0 pointer-events-none md:hidden"
        style={{
          backgroundImage: `url(${heroData.heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right -40px',
          opacity: 0.55,
          maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0) 95%)',
          WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0) 95%)'
        }}
      />

      {/* Hero visual — DESKTOP: right side, blended with mask fade */}
      <div
        className="absolute inset-y-0 right-0 w-full lg:w-[48%] xl:w-[46%] pointer-events-none opacity-[0.85] hidden md:block"
        style={{
          backgroundImage: `url(${heroData.heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
          maskImage: 'linear-gradient(to left, rgba(0,0,0,1) 15%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0) 100%)',
          WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,1) 15%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0) 100%)'
        }}
      />

      {/* Soft orbs — pushed further from text */}
      <div className="absolute -top-40 -left-60 w-[460px] h-[460px] rounded-full bg-violet-600/20 blur-[160px] animate-pulse-slow pointer-events-none" />
      <div className="absolute bottom-[-12%] right-[-12%] w-[600px] h-[600px] rounded-full bg-fuchsia-600/25 blur-[180px] animate-pulse-slow pointer-events-none" />

      {/* Readability veil — MOBILE: full, darker so text sits on a moody gradient */}
      <div
        className="absolute inset-0 pointer-events-none md:hidden"
        style={{
          background:
            'linear-gradient(180deg, rgba(11,17,32,0.55) 0%, rgba(11,17,32,0.72) 55%, rgba(11,17,32,0.92) 100%)'
        }}
      />

      {/* Readability veil — DESKTOP: left gradient behind text column */}
      <div
        className="absolute inset-y-0 left-0 w-full lg:w-[62%] pointer-events-none hidden md:block"
        style={{
          background:
            'linear-gradient(90deg, rgba(11,17,32,0.92) 0%, rgba(11,17,32,0.88) 35%, rgba(11,17,32,0.55) 70%, rgba(11,17,32,0) 100%)'
        }}
      />

      {/* Grain */}
      <div className="grain-overlay" />

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
          {/* Left: text (tighter column, more breathing) */}
          <div className="lg:col-span-7 xl:col-span-6 flex flex-col relative z-10">
            <motion.span
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={0}
              className="inline-flex items-center gap-2 self-start rounded-full border border-white/10 bg-white/[0.04] backdrop-blur-md px-4 py-1.5 mb-6"
              data-testid="hero-eyebrow"
            >
              <Sparkles className="w-3.5 h-3.5 text-fuchsia-300" />
              <span className="text-[11px] sm:text-xs font-semibold tracking-[0.18em] uppercase text-slate-200">
                {heroData.eyebrow}
              </span>
            </motion.span>

            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1}
              className="font-display text-[2.5rem] sm:text-5xl lg:text-[3.5rem] xl:text-[4rem] font-semibold text-white leading-[1.15] lg:leading-[1.02] tracking-tight text-balance drop-shadow-[0_2px_24px_rgba(0,0,0,0.55)]"
              data-testid="hero-title"
              style={{ overflow: 'visible' }}
            >
              {heroData.titleLine1}
              <span
                className="block mt-1.5 bg-clip-text text-transparent"
                style={{
                  backgroundImage: 'linear-gradient(120deg, #a78bfa 0%, #e879f9 45%, #f0abfc 100%)',
                  paddingBottom: '0.18em',
                  lineHeight: '1.18',
                  WebkitBoxDecorationBreak: 'clone',
                  overflow: 'visible'
                }}
              >
                {heroData.titleLine2}
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={2}
              className="mt-8 max-w-xl text-base sm:text-lg lg:text-xl text-slate-300/95 leading-relaxed"
              data-testid="hero-subtitle"
            >
              {heroData.subtitle}
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-8 flex flex-col sm:flex-row gap-3.5"
            >
              <button
                data-testid="hero-primary-cta"
                onClick={scrollToContact}
                className="shine group inline-flex items-center justify-center gap-2.5 rounded-full px-7 py-4 font-semibold text-white
                  bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:from-violet-500 hover:to-fuchsia-400
                  shadow-[0_12px_40px_-12px_rgba(217,70,239,0.55)] hover:shadow-[0_16px_50px_-10px_rgba(217,70,239,0.7)]
                  transition-all duration-400 hover:-translate-y-0.5"
              >
                {heroData.ctaPrimary}
                <ArrowRight className="w-4.5 h-4.5 transition-transform duration-300 group-hover:translate-x-1" />
              </button>

              <button
                data-testid="hero-secondary-cta"
                onClick={scrollToContact}
                className="group inline-flex items-center justify-center gap-2.5 rounded-full px-7 py-4 font-semibold text-white
                  border border-white/15 bg-white/[0.04] backdrop-blur-md hover:bg-white/[0.08] hover:border-white/25
                  transition-all duration-300"
              >
                {heroData.ctaSecondary}
                <ChevronRight className="w-4.5 h-4.5 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </motion.div>

            {/* Trust strip — pulled closer to buttons */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={4}
              className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-medium tracking-wider uppercase text-slate-400"
              data-testid="hero-trust-strip"
            >
              <span className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {heroData.trustItems[0]}
              </span>
              <span className="hidden sm:inline text-slate-700">·</span>
              <span>{heroData.trustItems[1]}</span>
              <span className="hidden sm:inline text-slate-700">·</span>
              <span>{heroData.trustItems[2]}</span>
            </motion.div>
          </div>

          {/* Right: floating stats card — moved higher & more to the right */}
          <div className="lg:col-span-5 xl:col-span-6 hidden lg:flex justify-end items-start relative">
            <motion.div
              initial={{ opacity: 0, y: 30, rotate: -2 }}
              animate={{ opacity: 1, y: 0, rotate: -2 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="animate-floaty -mt-24 xl:-mt-32 mr-[-12px] xl:mr-[-24px]"
            >
              <div className="glass rounded-2xl p-5 pr-6 w-[320px] shadow-[0_30px_80px_-25px_rgba(167,139,250,0.35)]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-white" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 12h4l3-8 4 16 3-8h4" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-400">Konverzió</div>
                    <div className="text-white font-semibold">+217% növekedés</div>
                  </div>
                </div>
                <div className="mt-4 h-px bg-white/10" />
                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-white font-display font-semibold text-lg">2.4s</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">LCP</div>
                  </div>
                  <div>
                    <div className="text-white font-display font-semibold text-lg">98</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">PageSpeed</div>
                  </div>
                  <div>
                    <div className="text-white font-display font-semibold text-lg">5.2x</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">ROAS</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#0b1120] to-transparent pointer-events-none" />
    </section>
  );
};

export default Hero;
